/**
 * Identity of an incident filing attempt, and of the triage it was based on.
 *
 * `createTicket` dedupes on "same Idempotency-Key + same body". The body an
 * incident files with carries two things the *server* chose on Continue — the
 * suggested subject and the category — and a second Continue can choose them
 * differently. Keyed on the whole body, a retry after a timeout would then get
 * a fresh key and raise a second ticket.
 *
 * So the attempt is identified by what the *person* wrote and chose, never by
 * what triage derived: the key stays the same for the life of the form until
 * they change the subject, details, attachments (either uploader's), impact, a
 * follow-up answer or the "Anything else?" `additional_details`. Triage is cached against its own inputs, and the result an
 * attempt first filed with is reused for its retries, so a retry sends the
 * byte-identical body the server replays.
 *
 * Pure and import-free so `node --test` can load it straight from source.
 */

/** The subject floor; mirrors `SUBJECT_MIN_LENGTH` in `validation.ts`. */
const OWN_SUBJECT_MIN_LENGTH = 3;

/** What an incident filing sends, as far as its identity is concerned. */
export interface IncidentAttemptBody {
  subject?: string | null;
  system?: string | null;
  /** The triage's Ivanti Service / Category names — server-derived too. */
  service_id?: string | null;
  category_id?: string | null;
  attachment_ids?: readonly string[] | null;
  [field: string]: unknown;
}

/** What `getIncidentFollowups` is asked about. */
export interface IncidentTriageInput {
  /** The person's own subject, already normalised by `ownIncidentSubject`. */
  subject: string | null;
  details: string;
  attachmentIds: readonly string[];
}

/**
 * The subject the person typed, or null when there is none worth sending.
 *
 * Shorter than the contract's floor counts as blank: the server writes a
 * subject from the description, which beats refusing to continue over "pc".
 */
export function ownIncidentSubject(subject: string): string | null {
  const trimmed = subject.trim();
  return trimmed.length < OWN_SUBJECT_MIN_LENGTH ? null : trimmed;
}

/** Cache key for one triage call: the same inputs get the same answer back. */
export function incidentTriageKey(input: IncidentTriageInput): string {
  return JSON.stringify([
    input.subject ?? "",
    input.details.trim(),
    [...input.attachmentIds].sort(),
  ]);
}

/**
 * Fingerprint of an attempt, for choosing its idempotency key.
 *
 * The server-derived subject (used only when the person left theirs blank),
 * the header category and the Ivanti Service/Category pair are left out;
 * attachment order is not significant. Every
 * other field is the person's input and counts — `additional_details`
 * included, which is omitted from the body (not sent blank) when unanswered.
 *
 * @param body The body about to be filed.
 * @param ownSubject The person's own subject, or null when the server wrote it.
 */
export function incidentAttemptFingerprint(
  body: IncidentAttemptBody,
  ownSubject: string | null,
): string {
  const rest = { ...body };
  delete rest.system;
  delete rest.service_id;
  delete rest.category_id;
  return JSON.stringify(
    Object.entries({
      ...rest,
      subject: ownSubject,
      attachment_ids: [...(body.attachment_ids ?? [])].sort(),
    }).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)),
  );
}
